import { alert } from 'actions/flash'
import {
  useGetMerchantOrderingConfigQuery,
  useUpdateMerchantOrderingConfigMutation,
} from 'api/orderingConfig'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { TimeBasedMenuVisibilitySetting } from 'models/Config'
import { useState } from 'react'

const t = buildTranslate('cms.settings.time_based_menu_visibility')

export type UseTimeBasedMenuVisibility = {
  hasCapability: boolean
  orderingEnabled: boolean
  setting: TimeBasedMenuVisibilitySetting | undefined
  isLoading: boolean
  isError: boolean
  isEditing: boolean
  isSubmitting: boolean
  startEditing: () => void
  stopEditing: () => void
  publish: (setting: TimeBasedMenuVisibilitySetting) => Promise<void>
}

const useTimeBasedMenuVisibility = (): UseTimeBasedMenuVisibility => {
  const merchant = useCurrentMerchant()
  const dispatch = useDispatch()
  const [isEditing, setIsEditing] = useState(false)

  const hasCapability = Boolean(merchant?.supports_time_based_menu_visibility)

  const { data, isLoading, isError } = useGetMerchantOrderingConfigQuery(
    undefined,
    { skip: !hasCapability }
  )
  const [updateOrderingConfig, { isLoading: isSubmitting }] =
    useUpdateMerchantOrderingConfigMutation()

  const publish = async (setting: TimeBasedMenuVisibilitySetting) => {
    try {
      await updateOrderingConfig({
        time_based_menu_visibility: setting,
      }).unwrap()
      dispatch(
        alert({ key: 'success', message: t('submit_success'), timeout: 5 })
      )
      setIsEditing(false)
    } catch {
      dispatch(alert({ key: 'danger', message: t('submit_error'), timeout: 5 }))
    }
  }

  return {
    hasCapability,
    orderingEnabled: Boolean(merchant?.ordering_enabled),
    setting: data?.ordering_config?.time_based_menu_visibility,
    isLoading,
    isError,
    isEditing,
    isSubmitting,
    startEditing: () => setIsEditing(true),
    stopEditing: () => setIsEditing(false),
    publish,
  }
}

export default useTimeBasedMenuVisibility
